import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { siteSettingsService } from '../../services/siteSettingsService';
import VendorSupportContent from '../../components/shared/VendorSupportContent';

const DEFAULT_HERO = { title: 'Vendor Support', subtitle: 'Everything you need to succeed as a vendor on VendaList' };
const DEFAULT_TABS = [
  { id: 'getting-started', label: 'Getting Started', icon: 'BookOpen' },
  { id: 'bookings', label: 'Managing Bookings', icon: 'Calendar' },
  { id: 'payments', label: 'Payments & Earnings', icon: 'CreditCard' },
  { id: 'profile', label: 'Profile & Services', icon: 'Settings' },
  { id: 'resources', label: 'Resources', icon: 'FileText' },
];
const DEFAULT_FAQS = {
  'getting-started': [
    { question: 'How do I create a vendor account?', answer: 'Click on "Become a Vendor" in the navigation menu, fill out the registration form with your business details, and verify your email.' },
    { question: 'What information do I need to provide?', answer: "You'll need business information (name, description, contact details), service categories, pricing, availability, and verification documents." },
    { question: 'How long does verification take?', answer: 'Verification typically takes 1-3 business days.' },
    { question: 'What are the platform fees?', answer: 'VendaList charges a commission fee on completed bookings. The exact percentage varies by subscription plan.' },
  ],
  bookings: [
    { question: 'How do I receive booking requests?', answer: "When clients are interested, they'll send booking requests through the platform. You'll receive notifications via email and in your dashboard." },
    { question: 'How do I accept or decline bookings?', answer: 'Go to your "Bookings" page, review the request details, and click "Accept" or "Decline".' },
    { question: 'Can I modify a booking after accepting?', answer: 'Yes, you can modify booking details up to 48 hours before the event.' },
    { question: 'What happens if I need to cancel a booking?', answer: 'Cancellations should be made as early as possible. Go to the booking details and select "Cancel Booking".' },
  ],
  payments: [
    { question: 'How do I get paid?', answer: 'Payments are processed automatically after service completion. Funds are transferred within 5-7 business days.' },
    { question: 'What payment methods are supported?', answer: 'We support bank transfers, PayPal, and other payment processors.' },
    { question: 'How do I view my earnings?', answer: 'Go to your "Earnings" or "Payments" section in the vendor dashboard.' },
    { question: 'Are there any payment fees?', answer: 'Payment processing fees may apply depending on your payment method.' },
  ],
  profile: [
    { question: 'How do I update my vendor profile?', answer: 'Go to "Vendor Profile" in your dashboard, click "Edit Profile", and update your information.' },
    { question: 'How do I add or edit services?', answer: 'Navigate to "Services" in your dashboard, click "Add Service" or edit an existing service.' },
    { question: 'How do I manage my portfolio?', answer: 'Go to "Portfolio" in your dashboard to upload photos and videos of your work.' },
    { question: 'Can I set my own availability?', answer: 'Yes, go to "Availability" in your dashboard to set your working hours and blackout dates.' },
  ],
  resources: [
    { question: 'Where can I find marketing resources?', answer: 'Visit the "Resources" section in your vendor dashboard for marketing templates and tips.' },
    { question: 'Are there training materials available?', answer: 'Yes, we provide video tutorials, webinars, and written guides.' },
    { question: 'How do I access vendor analytics?', answer: 'Go to "Analytics" in your dashboard to view booking trends and performance metrics.' },
    { question: 'Is there a vendor community?', answer: 'Yes! Join our vendor community forum to connect with other vendors.' },
  ],
};
const DEFAULT_CONTACT = [
  { icon: 'MessageSquare', title: 'Live Chat', description: 'Chat with our vendor support team', action: 'Start Chat', available: true },
  { icon: 'Mail', title: 'Email Support', description: 'vendors@vendalist.com', action: 'Send Email', available: true },
  { icon: 'Phone', title: 'Phone Support', description: '1-800-VENDA-VENDOR', action: 'Call Now', available: true },
];
const DEFAULT_QUICK_LINKS = [
  { icon: 'Settings', title: 'Vendor Dashboard', description: 'Manage your account and services', href: '/vendor/dashboard' },
  { icon: 'Calendar', title: 'Booking Calendar', description: 'View and manage your bookings', href: '/vendor/calendar' },
  { icon: 'CreditCard', title: 'Earnings & Payments', description: 'Track your revenue and payments', href: '/vendor/earnings' },
  { icon: 'Users', title: 'Client Messages', description: 'Communicate with your clients', href: '/vendor/messages' },
];

const VendorSupport = () => {
  const { data: response, isLoading } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: () => siteSettingsService.get(),
  });

  const vs = response?.data?.vendor_support_settings;
  const hasSettings = vs && typeof vs === 'object' && (
    (Array.isArray(vs.tabs) && vs.tabs.length > 0) ||
    (vs.hero && (vs.hero.title || vs.hero.subtitle))
  );

  const hero = hasSettings && vs.hero ? vs.hero : DEFAULT_HERO;
  const tabs = hasSettings && Array.isArray(vs.tabs) && vs.tabs.length > 0 ? vs.tabs : DEFAULT_TABS;
  const faqs = hasSettings && vs.faqs && typeof vs.faqs === 'object' ? vs.faqs : DEFAULT_FAQS;
  const contact_options = hasSettings && Array.isArray(vs.contact_options) ? vs.contact_options : DEFAULT_CONTACT;
  const quick_links = hasSettings && Array.isArray(vs.quick_links) ? vs.quick_links : DEFAULT_QUICK_LINKS;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <VendorSupportContent
      hero={hero}
      tabs={tabs}
      faqs={faqs}
      contact_options={contact_options}
      quick_links={quick_links}
      searchPlaceholder="Search vendor support..."
      showSearch={true}
    />
  );
};

export default VendorSupport;
